import React from 'react'
import { alphabeticSection } from '../utils/sectioned-input/alphabetic-section'
import { numericSection } from '../utils/sectioned-input/numeric-section'
import { BuildUseSectionedInput } from '../utils/sectioned-input/use-sectioned-input'

export interface HeadlessTimeInputProps extends Omit<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange'
> {
  value?: string | null
  defaultValue?: string | null
  onChange?(value: string | null): unknown
}

export const HeadlessTimeInput = React.forwardRef<HTMLInputElement, HeadlessTimeInputProps>(
  ({ value, defaultValue, onChange, ...props }, ref) => {
    const { inputProps } = useTimeInput({
      value,
      defaultValue,
      onChange
    })
    return <input {...props} {...inputProps} ref={ref} />
  }
)
HeadlessTimeInput.displayName = 'HeadlessTimeInput'

const useTimeInput = BuildUseSectionedInput({
  sections: [
    numericSection({ min: 1, max: 12, placeholder: 'hh', suffix: ':', pad: true }),
    numericSection({
      min: 0,
      max: 60,
      placeholder: 'mm',
      suffix: ' ',
      pad: true
    }),
    alphabeticSection({
      options: ['am', 'pm'],
      placeholder: '_m',
      suffix: ''
    })
  ],
  externalValueToSectionValues(value) {
    if (!value) return ['', '', '']
    try {
      const date = new Date(`2000-01-01T${value}Z`)
      const hours = date.getUTCHours()
      return [(hours % 12 || 12).toString(), date.getUTCMinutes().toString().padStart(2, '0'), hours > 11 ? 'pm' : 'am']
    } catch {
      return ['', '', '']
    }
  },
  sectionValuesToExternalValue(sectionValues) {
    const [hourStr, minuteStr, amPmStr] = sectionValues
    try {
      let date = new Date()
      date.setUTCHours(amPmStr === 'am' ? Number(hourStr) % 12 : (Number(hourStr) % 12) + 12, Number(minuteStr), 0, 0)
      const result = date.toISOString().slice(11, -1)
      return result
    } catch {
      return null
    }
  },
  tryParsePaste(value) {
    try {
      const date = new Date(`01/01/2000 ${value}`)
      if (!Number.isNaN(date.getTime())) {
        const hours = date.getHours()
        return [`${hours % 12 || 12}`, `${date.getMinutes()}`.padStart(2, '0'), hours > 11 ? 'pm' : 'am']
      } else {
        return null
      }
    } catch {
      return null
    }
  }
})
